import React from 'react';
import PropTypes from 'prop-types';
import { currentSearch, decodeQuery } from 'Helpers/Query';
import useCollection from 'Hooks/useCollection';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { householdSecureFilesPreviewUri } from 'Consts/sharedUris';

import { useHistory } from 'react-router';
import { useSnackbarContext } from 'Context/SnackbarContext';
import fr from 'facet-rest';
import { openModal } from 'Helpers/ModalHelper';
import { buildModalQuery } from 'Consts/ModalQueries';
import modalConsts from 'Consts/ModalNames';
import { navigateToClientPage } from 'Views/Page/Content/Templates';
import { navigateToFunding } from 'Helpers/AccountFunding';

const MODAL_PATH_QUERY_ACCOUNT = 'modal=account';

const MARK_COMPLETE_MS_TIMEOUT = 10000;

function ListItemContainer({
  resource, Component, refreshActionItems, replaceActionItem,
}) {
  const { householdResource } = useSimpleHousehold();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  const [loading, setLoading] = React.useState(true);
  const {
    collection: fileLinkCollection,
    setCollection: setFileLinkCollection,
  } = useCollection();
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const [expand, setExpand] = React.useState(false);
  const [fileExpand, setFileExpand] = React.useState(false);
  const [showMarkCompleteTooltip, setShowMarkCompleteTooltip] = React.useState(false);
  const [previousLocationSearch, setPreviousLocationSearch] = React.useState('');
  const [hasAccounts, setHasAccounts] = React.useState(null);
  const [selectedFileId, setSelectedFileId] = React.useState('');
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [alternativeToggleDisplay, setAlternativeToggleDisplay] = React.useState(false);

  const history = useHistory();
  const timeoutRef = React.useRef();

  const hidden = resource.attr('hidden') === 'true';
  const status = resource.attr('status');
  const name = resource.attr('name');
  const completed = resource.attr('status') === 'completed';

  function setProgress(progress) {
    if (progress === null) {
      setUploadProgress(null);
    } else {
      const diff = Math.random() * 10;
      setUploadProgress(Math.min(progress + diff, 100));

      if (progress + diff >= 100 && status !== 'complete') {
        setShowMarkCompleteTooltip(true);
      }
    }
  }

  function handleDeleteFile(file) {
    if (file.id() === selectedFileId) {
      setSelectedFileId('');
    }

    return file
      .link('self')
      .Delete()
      .then(() => fileLinkCollection.refresh())
      .then(coll => setFileLinkCollection(coll))
      .then(() => displaySuccess('Successfully removed file'))
      .catch((e) => {
        const msg = e.response?.body?.errors ? e.response?.body?.errors : 'Failed to delete file';
        displayErrorMessage(e, msg);
      });
  }

  function handleFileClick(file) {
    const currentQueries = decodeQuery(currentSearch());
    history.push(
      householdSecureFilesPreviewUri(
        householdResource.id(),
        [file.id()],
        currentQueries,
      ),
    );

    return null;
  }

  const toggleHide = () => {
    const nextHidden = !hidden;
    const updateResource = resource.copy().attr('hidden', nextHidden ? 'true' : 'false');
    updateResource
      .submit({
        linkArgs: {
          raw: true,
        },
      })
      .then(() => refreshActionItems())
      .then(() => {
        if (nextHidden) {
          displaySuccess(`${name} has been hidden.`);
        } else {
          displaySuccess(`${name} has been unhidden.`);
        }
      })
      .catch(e => displayErrorMessage(e, e.errors || `failed to hide ${name}`));
  };

  const markAsComplete = async () => {
    if (showMarkCompleteTooltip) setShowMarkCompleteTooltip(false);

    const updateResource = resource.copy().attr('status', 'completed');

    try {
      const resp = await updateResource.submit();
      replaceActionItem(resp);
      displaySuccess(`${name} has been completed!`);
    } catch (err) {
      displayErrorMessage(err, err.errors || `failed to mark ${name} complete`);
    }
  };

  async function refreshHasAccounts() {
    if (!householdResource) {
      return Promise.resolve();
    }
    const newAccountCollection = await householdResource.relationship(fr.relationships.accounts.name).link('related').get();
    setHasAccounts(newAccountCollection.resources().list().length > 0);
    return Promise.resolve();
  }

  // ACCOUNT SETUP -----------------------------------------------------------------------------------------------------

  const isAccountFundingAi = resource.attr('actionItemType') === 'fund-account';
  const isAccountAi = Boolean(resource?.relationship(fr.relationships.accountOpeningWizard.name)?.link('related'));
  const isAccountRetitlingAi = resource.withRelationship(fr.relationships.accountRetitlingWizard.name, () => true, () => false);
  const trustApprovalRelationship = resource.relationship(fr.relationships.approval.name)?.link('related');
  const isTrustApproval = Boolean(trustApprovalRelationship);

  // Account funding
  const handleAccountFundingOnClick = () => {
    if (!completed) {
      navigateToFunding(resource);
    }
  };

  async function handleAccountWizardClick(relationship) {
    const [accountWizardStep] = await relationship.link('related').get();
    navigateToClientPage(accountWizardStep.link('self').url());
  }

  // Launch account application
  const handleAccountApplicationOnClick = () => {
    const relationship = resource.withRelationship(fr.relationships.accountOpeningWizard.name, (rel) => rel, () => null);
    if (!completed && relationship !== null) {
      return handleAccountWizardClick(relationship);
    }
    return null;
  };

  const handleAccountRetitlingOnClick = () => {
    const relationship = resource.withRelationship(fr.relationships.accountRetitlingWizard.name, (rel) => rel, () => null);
    if (!completed && relationship !== null) {
      return handleAccountWizardClick(relationship);
    }
    return null;
  };

  const handleTrustApprovalOnClick = () => {
    openModal(
      buildModalQuery(modalConsts.trustApproval, {
        approvalLink: trustApprovalRelationship.url(),
      }),
    );
  };

  const getActionItemOnClick = () => {
    if (isAccountAi || isAccountRetitlingAi) {
      if (completed) return null;
      if (isAccountRetitlingAi) return handleAccountRetitlingOnClick();
      if (isAccountFundingAi) return handleAccountFundingOnClick();
      if (!isAccountFundingAi) return handleAccountApplicationOnClick();
      if (isTrustApproval) return handleTrustApprovalOnClick();
    }
    return null;
  };

  React.useEffect(() => {
    if (resource) {
      const link = resource.relationship('getSecureFiles')?.link('related');

      if (link) {
        link.Get().then((res) => {
          setFileLinkCollection(res);
        });
      }
    }
  }, [resource]);

  React.useEffect(() => {
    if (showMarkCompleteTooltip) {
      timeoutRef.current = setTimeout(() => {
        setShowMarkCompleteTooltip(false);
      }, MARK_COMPLETE_MS_TIMEOUT);
    }
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [showMarkCompleteTooltip]);

  React.useEffect(() => {
    refreshHasAccounts().catch(e => displayErrorMessage(e, e.errors));
  }, [householdResource]);

  React.useEffect(() => {
    //  When we close the account modal, check to see if we now have accounts.
    if (previousLocationSearch.includes(MODAL_PATH_QUERY_ACCOUNT) && !history.location.search.includes(MODAL_PATH_QUERY_ACCOUNT)) {
      refreshHasAccounts().catch(e => displayErrorMessage(e, e.errors));
    }
    setPreviousLocationSearch(history.location.search);
  }, [history.location]);

  return (
    <Component
      loading={loading}
      uploadProgress={uploadProgress}
      setProgress={setProgress}
      setFileLinkCollection={setFileLinkCollection}
      fileLinkCollection={fileLinkCollection}
      refreshHasAccounts={refreshHasAccounts}
      markAsComplete={markAsComplete}
      handleFileClick={handleFileClick}
      handleDeleteFile={handleDeleteFile}
      setLoading={setLoading}
      expand={expand}
      setExpand={setExpand}
      setFileExpand={setFileExpand}
      fileExpand={fileExpand}
      hasAccounts={hasAccounts}
      completed={completed}
      selectedFileId={selectedFileId}
      setSelectedFileId={setSelectedFileId}
      selectedAccountId={selectedAccountId}
      setSelectedAccountId={setSelectedAccountId}
      toggleHide={toggleHide}
      hidden={hidden}
      showMarkCompleteTooltip={showMarkCompleteTooltip}
      alternativeToggleDisplay={alternativeToggleDisplay}
      setAlternativeToggleDisplay={setAlternativeToggleDisplay}
      actionItemOnClick={getActionItemOnClick}
      isAccountAi={isAccountAi}
      isAccountRetitlingAi={isAccountRetitlingAi}
      refreshActionItems={refreshActionItems}
      resource={resource}
    />);
}

ListItemContainer.propTypes = {
  replaceActionItem: PropTypes.func.isRequired,
  refreshActionItems: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
};

export default ListItemContainer;
